/**
 * User Portal Authentication Context
 * Manages user (participant) authentication state
 * Separate from admin auth context
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserAuthContext = createContext(null);

export const UserAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  
  // Ref to track if validation is in progress
  const validationInProgress = useRef(false);
  // Ref for abort controller
  const abortControllerRef = useRef(null);

  // Memoized clear auth function
  const clearAuth = useCallback(() => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Memoized token validation
  const validateToken = useCallback(async (authToken) => {
    // Prevent duplicate validation calls
    if (validationInProgress.current) return;
    validationInProgress.current = true;
    
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    try {
      const response = await fetch(`${API_URL}/api/user/auth/me`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        signal: abortControllerRef.current.signal
      });
      
      if (!response.ok) {
        // Token invalid or expired
        clearAuth();
      }
    } catch (error) {
      // Ignore abort errors
      if (error.name !== 'AbortError') {
        console.error('Token validation failed:', error);
      }
    } finally {
      validationInProgress.current = false;
    }
  }, [clearAuth]);

  // Check for existing token on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('userToken');
    const storedUser = localStorage.getItem('userData');
    
    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(userData);
        setIsAuthenticated(true);
        
        // Validate token in background (non-blocking)
        validateToken(storedToken);
      } catch (e) {
        // Invalid stored data
        clearAuth();
      }
    }
    setLoading(false);
    
    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [validateToken, clearAuth]);

  const login = useCallback((authToken, userData) => {
    localStorage.setItem('userToken', authToken);
    localStorage.setItem('userData', JSON.stringify(userData));
    setToken(authToken);
    setUser(userData);
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(async () => {
    try {
      if (token) {
        // Fire and forget - don't block logout on API response
        fetch(`${API_URL}/api/user/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }).catch(() => {}); // Ignore errors
      }
    } catch (error) {
      // Ignore errors
    }
    clearAuth();
  }, [token, clearAuth]);

  const updateUser = useCallback((newUserData) => {
    setUser(prevUser => {
      const updatedUser = { ...prevUser, ...newUserData };
      localStorage.setItem('userData', JSON.stringify(updatedUser));
      return updatedUser;
    });
  }, []);

  return (
    <UserAuthContext.Provider value={{
      user,
      token,
      isAuthenticated,
      loading,
      login,
      logout,
      updateUser
    }}>
      {children}
    </UserAuthContext.Provider>
  );
};

export const useUserAuth = () => {
  const context = useContext(UserAuthContext);
  if (!context) {
    throw new Error('useUserAuth must be used within a UserAuthProvider');
  }
  return context;
};

export default UserAuthContext;
